import React, { useState, useEffect } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer/Footer';
import SearchSymbol from '../../components/SearchSymbol/SearchSymbol';
import NewOrderButton from '../../components/NewOrder/NewOrderButton';
import NewOrderModal from '../../components/NewOrder/NewOrderModal';
import OrderRow from './OrderRow';
import { getOrders, deleteOrder, clearOrders } from '../../services/OrdersService';
import Pagination from '../../components/Pagination/Pagination';
import ViewOrderModal from './ViewOrderModal';
import Toast from '../../components/Toast/Toast';
import PositionsTable from '../Futures/PositionsTable';
import { useIntl } from 'react-intl';
import logger from '../../utils/logger';

function Orders() {

    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });

    const defaultLocation = useLocation();

    function getPage(location) {
        if (!location) location = defaultLocation;
        return new URLSearchParams(location.search).get('page');
    }

    const history = useHistory();

    useEffect(() => {
        return history.listen((location) => {
            setPage(getPage(location));
        })
    }, [history])

    const { symbol } = useParams();

    const [search, setSearch] = useState(symbol ? symbol : '');
    const [orders, setOrders] = useState([]);
    const [notification, setNotification] = useState([]);
    const [count, setCount] = useState(0);
    const [viewOrder, setViewOrder] = useState({});
    const [page, setPage] = useState(getPage());
    const [isLoading, setIsLoading] = useState(false);
    const [refresh, setRefresh] = useState(0);

    function showFutures() {
        return window.location.href.indexOf('forders') !== -1;
    }

    useEffect(() => {
        setIsLoading(true);
        getOrders(search, page || 1, showFutures())
            .then(result => {
                setOrders(result.rows);
                setCount(result.count);
                setViewOrder(result.rows[0]);
                setIsLoading(false);
            })
            .catch(err => {
                logger.error(err.response ? err.response.data : err.message);
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
                setIsLoading(false);
            })
    }, [search, page, refresh])

    function onSearchChange(event) {
        setSearch(event.target.value);
    }

    function onViewClick(event) {
        const id = event.target.id.replace('view', '');
        const order = orders.find(o => o.id == id);
        // eslint-disable-next-line
        setViewOrder({ ...order });
    }

    function onOrderSubmit(order) {
        setRefresh(Date.now());
    }

    function onDeleteClick(id) {
        if (!window.confirm(t('orders.deleteConfirm'))) return;

        deleteOrder(id)
            .then(() => {
                setNotification({ type: 'success', text: t('orders.deleted') });
                setRefresh(Date.now());
            })
            .catch(err => {
                logger.error(err.response ? err.response.data : err.message);
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
            })
    }

    function onClearClick() {
        if (!window.confirm(t('orders.clearConfirm'))) return;

        clearOrders(showFutures())
            .then(() => {
                setNotification({ type: 'success', text: t('orders.cleared') });
                setRefresh(Date.now());
            })
            .catch(err => {
                logger.error(err.response ? err.response.data : err.message);
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
            })
    }

    function toggleMarket() {
        if (showFutures())
            history.push('/orders/');
        else
            history.push('/forders/');
        setRefresh(Date.now());
    }

    return (
        <React.Fragment>
            <Menu />
            <main className="content">
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className="d-block mb-4 mb-md-0">
                        <h2 className="h4 d-inline-block me-2">{showFutures() ? t('orders.futuresTitle') : t('orders.spotTitle')}</h2>
                        <button type="button" className="btn btn-sm btn-danger" onClick={onClearClick}>{t('orders.clearAll')}</button>
                    </div>
                    <div className="btn-toolbar mb-2 mb-md-0">
                        {
                            localStorage.getItem("hasFutures") === "true"
                                ? (
                                    <div className='me-2 mb-3'>
                                        <button className='btn btn-secondary me-2' onClick={toggleMarket}>
                                            {showFutures() ? t('common.futures') : t('common.spot')}
                                        </button>
                                    </div>
                                )
                                : <React.Fragment></React.Fragment>
                        }
                        <div className="d-inline-flex align-items-center me-2 mb-3">
                            <NewOrderButton />
                        </div>
                        <div className="btn-group">
                            <SearchSymbol onChange={onSearchChange} placeholder={search} />
                        </div>
                    </div>
                </div>
                <div className="card card-body border-0 shadow table-wrapper table-responsive">
                    <table className="table table-hover gh-table-stack">
                        <thead>
                            <tr>
                                <th className="border-gray-200">{t('orders.order')}</th>
                                <th className="border-gray-200">{t('orders.date')}</th>
                                <th className="border-gray-200">{t('orders.qty')}</th>
                                <th className="border-gray-200">{t('orders.net')}</th>
                                <th className="border-gray-200">{t('common.status')}</th>
                                <th className="border-gray-200">{t('orders.view')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                isLoading
                                    ? <tr><td colSpan={6}>{t('common.loading')}</td></tr>
                                    : (orders && orders.length
                                        ? orders.map(order => (<OrderRow key={order.clientOrderId} data={order} onClick={onViewClick} onDelete={onDeleteClick} />))
                                        : <tr><td colSpan={6} className="text-muted">{t('orders.empty')}</td></tr>)
                            }
                        </tbody>
                    </table>
                    <Pagination count={count} />
                </div>
                {
                    showFutures()
                        ? <PositionsTable />
                        : <React.Fragment></React.Fragment>
                }
                <Footer />
            </main>
            <ViewOrderModal data={viewOrder} onUpdate={onOrderSubmit} />
            <NewOrderModal onSubmit={onOrderSubmit} initialSymbol={symbol} />
            <Toast type={notification.type} text={notification.text} />
        </React.Fragment>
    );
}

export default Orders;
